import './Login.scss';

import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import ButtonLoading from '@/common/components/molecules/ButtonLoading/ButtonLoading';
import Snack from '@/common/components/molecules/Snack/Snack';
import LoginForm from '@/common/components/organisms/LoginForm/LoginForm';
import { ROUTE_SCANBAT_LOGIN } from '@/common/constants';
import { useAuth } from '@/common/contexts/AuthContext';
import getRecaptchaToken from '@/common/helpers/getRecaptchaToken';
import useRegisterScanbat from '@/common/mutations/register/useRegisterScanbat';
import SessionStorageAuth from '@/common/services/SessionStorage.auth';
import ForgotPassword from '@/ebat/components/blocks/Modal/Common/ForgotPassword/ForgotPassword';
import QrCode from '@/ebat/components/blocks/Modal/Scanbat/QrCode/QrCode';
import Header from '@/ebat/components/blocks/Scanbat/Common/Header/Header';
import { ReactComponent as ErrorIcon } from '@/materiaux/assets/icons/warning.svg';

import { LoginDefaultProps, LoginPropTypes } from './Login.proptypes';
import useLogin from './useLogin';

const Login = ({
  company,
  disabled,
  errors,
  onSubmit,
  paramIsOpen,
  warehouse,
}) => {
  const { t } = useTranslation();

  const {
    onCloseForgotPassword,
    onOpenForgotPassword,
    onRemoveWarehouse,
    onScanQrCode,
  } = useLogin();

  const authService = SessionStorageAuth.getService();

  const { setIsLoggedIn } = useAuth();

  const { mutate: mutateRegisterScanbat, isLoading } = useRegisterScanbat();

  const handleRegisterGuest = (recaptchaToken) => {
    mutateRegisterScanbat(
      { company, warehouse: warehouse.code, recaptchaToken },
      {
        onError: () => {
          toast.error(<Snack message={t('common:messages:auth.guest')} />);
        },
        onSuccess: (res) => {
          authService.setToken(res.token, res.refresh_token);
          setIsLoggedIn(true);
        },
      },
    );
  };

  const handleGuest = () => {
    if (process.env.REACT_APP_RECAPTCHA_ACTIVE === 'true') {
      getRecaptchaToken()
        .then((recaptchaToken) => handleRegisterGuest(recaptchaToken))
        .catch((error) => {
          toast.error(
            <Snack
              message={t('common:messages:forms.errors.recaptcha_error')}
            />,
          );
          throw error;
        });
    } else {
      // Utiliser le token temporaire si reCAPTCHA est désactivé
      handleRegisterGuest(process.env.REACT_APP_RECAPTCHA_TOKEN);
    }
  };

  return (
    <>
      <div className="wrapper wrapper--full">
        <Header
          className="scanbat-login-page__header"
          company={company}
          disabled={disabled}
          link={ROUTE_SCANBAT_LOGIN}
          onRemoveWarehouse={onRemoveWarehouse}
          warehouse={warehouse}
        />
        <h1 className="h1 scanbat-login-page__title" data-qa-id="title-h1">
          {t('ebat:entities:scanbat.login.description.title')}
        </h1>
        <div className="scanbat-login-page__connect">
          <LoginForm
            className="scanbat-login-page__form"
            company={company}
            disabled={disabled}
            errors={errors}
            errorsIcon={<ErrorIcon />}
            onForgotPassword={onOpenForgotPassword}
            onSubmit={onSubmit}
            title={t('ebat:entities:scanbat.login.connect.title')}
            warehouse={warehouse?.code}
          />
          {warehouse?.customer_code && (
            <ButtonLoading
              className="scanbat-login-page__connect-guest"
              disabled={disabled}
              onClick={handleGuest}
              variant="primary-light"
              isLoading={isLoading}
            >
              {t('common:messages:actions.connect_guest')}
            </ButtonLoading>
          )}
        </div>
      </div>
      <ForgotPassword closeModal={onCloseForgotPassword} isOpen={paramIsOpen} />
      {!paramIsOpen && (
        <QrCode isOpen={!warehouse} onScanQrCode={onScanQrCode} />
      )}
    </>
  );
};

Login.defaultProps = {
  ...LoginDefaultProps,
};

Login.propTypes = {
  ...LoginPropTypes,
};

export default Login;
